import React, { useState, useEffect, useCallback } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import axiosInstance from "../axiosConfig";
import Select from "react-select";
import Swal from "sweetalert2";
import "../assets/CSS/Dashboard.css";

// ─── STATUS BADGE ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    "Complete":          { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
    "Completed":         { bg: "#d1fae5", color: "#065f46", dot: "#10b981" },
    "In Progress":       { bg: "#dbeafe", color: "#1e40af", dot: "#3b82f6" },
    "Awaiting Confirm":  { bg: "#f3f4f6", color: "#4b5563", dot: "#9ca3af" },
  };
  const s = map[status] || map["Awaiting Confirm"];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color,
      padding: "4px 12px", borderRadius: 20,
      fontSize: 12, fontWeight: 600, whiteSpace: "nowrap"
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
      {status}
    </span>
  );
};

// ─── PHOTO THUMBNAIL ───────────────────────────────────────────────────────────
const PhotoCell = ({ src, label, onView }) => {
  const BASE = process.env.REACT_APP_API_BASE_URL || "https://wip.fibrebondindustries.com/backend";
  if (!src) return (
    <span style={{ color: "#9ca3af", fontSize: 12, fontStyle: "italic" }}>— No photo</span>
  );
  return (
    <button onClick={() => onView(BASE + src)} style={{
      border: "none", background: "none", cursor: "pointer", padding: 0
    }}>
      <img
        src={BASE + src}
        alt={label}
        style={{
          width: 56, height: 56, objectFit: "cover",
          borderRadius: 8,
          border: "2px solid #e5e7eb",
          transition: "all 0.2s ease-in-out",
          display: "block",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "scale(1.15)";
          e.currentTarget.style.boxShadow = "0 8px 16px rgba(0,0,0,0.1)";
          e.currentTarget.style.borderColor = "#2563eb";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)";
          e.currentTarget.style.borderColor = "#e5e7eb";
        }}
      />
    </button>
  );
};

// ─── LIGHTBOX ─────────────────────────────────────────────────────────────────
const Lightbox = ({ src, onClose }) => {
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
  if (!src) return null;
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.85)",
      zIndex: 999999, display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div onClick={e => e.stopPropagation()} style={{ position: "relative", maxWidth: "90vw", maxHeight: "90vh" }}>
        <img src={src} alt="Preview" style={{
          maxWidth: "90vw", maxHeight: "85vh",
          borderRadius: 12, boxShadow: "0 25px 60px rgba(0,0,0,.6)",
          display: "block"
        }} />
        <button onClick={onClose} style={{
          position: "absolute", top: -16, right: -16,
          background: "#ef4444", color: "#fff", border: "none",
          borderRadius: "50%", width: 32, height: 32,
          fontSize: 16, cursor: "pointer", lineHeight: "32px",
          fontWeight: 700, boxShadow: "0 4px 12px rgba(0,0,0,.3)",
          display: "flex", alignItems: "center", justifyContent: "center"
        }}>✕</button>
        <a href={src} download target="_blank" rel="noreferrer" style={{
          position: "absolute", bottom: -50, left: "50%", transform: "translateX(-50%)",
          background: "#2563eb", color: "#fff", borderRadius: 24,
          padding: "8px 24px", fontSize: 14, fontWeight: 600,
          textDecoration: "none", display: "inline-block",
          boxShadow: "0 4px 12px rgba(0,0,0,.3)",
          transition: "background 0.2s"
        }}
        onMouseEnter={e => e.currentTarget.style.background = "#1d4ed8"}
        onMouseLeave={e => e.currentTarget.style.background = "#2563eb"}
        >⬇ Download Image</a>
      </div>
    </div>
  );
};

// ─── STAT CARD ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color }) => (
  <div style={{
    background: "#fff", borderRadius: 16,
    padding: "20px 24px",
    boxShadow: "0 4px 20px rgba(0,0,0,.03)",
    borderLeft: `5px solid ${color}`,
    display: "flex", alignItems: "center", gap: 16, flex: 1,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "default"
  }}
  onMouseEnter={e => {
    e.currentTarget.style.transform = "translateY(-4px)";
    e.currentTarget.style.boxShadow = "0 12px 24px rgba(0,0,0,.08)";
  }}
  onMouseLeave={e => {
    e.currentTarget.style.transform = "translateY(0)";
    e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.03)";
  }}
  >
    <div style={{
      background: `${color}15`, width: 56, height: 56, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 28, fontWeight: 800, color: "#1f2937", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", marginTop: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
    </div>
  </div>
);

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function WipPhotoLogs() {
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const [rows, setRows]           = useState([]);
  const [filters, setFilters]     = useState({ supervisors: [], processes: [] });
  const [loading, setLoading]     = useState(true);
  const [lightbox, setLightbox]   = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

      // Filter state
  const [fSupervisor, setFSupervisor] = useState(null);
  const [fProcess,    setFProcess]    = useState(null);
  const [fDateFrom,   setFDateFrom]   = useState("");
  const [fDateTo,     setFDateTo]     = useState("");
  const [fStatus,     setFStatus]     = useState("");
  const [search,      setSearch]      = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset to page 1 on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [fSupervisor, fProcess, fDateFrom, fDateTo, fStatus, search, itemsPerPage]);

  // Fetch filter options once
  useEffect(() => {
    axiosInstance.get("/api/wip-photo-logs/filters")
      .then(r => setFilters(r.data))
      .catch(console.error);
  }, []);

  // Fetch rows on filter change
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fSupervisor?.value) params.supervisor = fSupervisor.value;
      if (fProcess?.value)    params.process    = fProcess.value;
      if (fDateFrom)          params.dateFrom   = fDateFrom;
      if (fDateTo)            params.dateTo     = fDateTo;
      if (fStatus)            params.status     = fStatus;
      const { data } = await axiosInstance.get("/api/wip-photo-logs", { params });
      setRows(data);
      setSelectedIds([]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [fSupervisor, fProcess, fDateFrom, fDateTo, fStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Client-side search
  const visible = rows.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.LotID        || "").toLowerCase().includes(q) ||
      (r.SupervisorName || "").toLowerCase().includes(q) ||
      (r.ProcessName  || "").toLowerCase().includes(q)
    );
  });

  // Stats
  const total       = rows.length;
  const complete    = rows.filter(r => r.PhotoStatus === "Complete" || r.PhotoStatus === "Completed").length;
  const inProgress  = rows.filter(r => r.PhotoStatus === "In Progress").length;
  const missing     = rows.filter(r => (r.ConfirmTime && !r.BeforePhoto) || (r.CompletedTime && !r.AfterPhoto)).length;

  // Pagination logic
  const totalPages = itemsPerPage === "All" ? 1 : Math.ceil(visible.length / itemsPerPage);
  const paginatedRows = itemsPerPage === "All" ? visible : visible.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const clearFilters = () => {
    setFSupervisor(null); setFProcess(null); setFDateFrom(""); setFDateTo(""); setFStatus(""); setSearch("");
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedIds.length} records. This will also remove the photos permanently!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete them!"
    });
    
    if (!result.isConfirmed) return;

    try {
      await axiosInstance.delete("/api/wip-photo-logs/bulk", { data: { ids: selectedIds } });
      Swal.fire({ title: "Deleted!", text: "Records have been deleted successfully.", icon: "success", timer: 2000, showConfirmButton: false });
      fetchData();
    } catch (e) {
      console.error(e);
      Swal.fire({ title: "Error", text: "Something went wrong while deleting.", icon: "error" });
    }
  };

  const handleDeleteSingle = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You are about to delete this record and its photos permanently!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, delete it!"
    });
    
    if (!result.isConfirmed) return;
    
    try {
      await axiosInstance.delete("/api/wip-photo-logs/bulk", { data: { ids: [id] } });
      Swal.fire({ title: "Deleted!", text: "Record has been deleted successfully.", icon: "success", timer: 2000, showConfirmButton: false });
      fetchData();
    } catch (e) {
      console.error(e);
      Swal.fire({ title: "Error", text: "Something went wrong while deleting.", icon: "error" });
    }
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex dashboard">
      <Lightbox src={lightbox} onClose={() => setLightbox(null)} />

      {/* ── SIDEBAR ── */}
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* ── HEADER ── */}
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        {/* ── MAIN CONTENT AREA ── */}
        <main className="main-container p-0 pb-4" style={{ background: "#f3f5f8", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          
          {/* ── PAGE BANNER ── */}
          <div style={{
            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
            padding: "32px 40px", color: "#fff",
            boxShadow: "0 4px 12px rgba(37, 99, 235, 0.15)"
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
              <div>
                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
                  <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{marginRight: 12}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                  WIP Photo Audit Hub
                </h1>
                <p style={{ margin: "6px 0 0", opacity: 0.9, fontSize: 15, fontWeight: 500 }}>
                  Real-time visual verification for WIP lot confirmations across all departments.
                </p>
              </div>
              <button
                onClick={fetchData}
                style={{
                  background: "rgba(255,255,255,0.2)", color: "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                  borderRadius: 10, padding: "10px 20px",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                  backdropFilter: "blur(10px)",
                  transition: "all 0.2s",
                  display: "flex", alignItems: "center", gap: 8
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.3)"}
                onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.2)"}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path></svg>
                Refresh Data
              </button>
            </div>
          </div>

          <div style={{ padding: "32px 40px" }}>

            {/* ── STAT CARDS ── */}
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 32 }}>
              <StatCard label="Total Lots Processed" value={total}      icon={<svg width="28" height="28" fill="none" stroke="#6366f1" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>} color="#6366f1" />
              <StatCard label="Fully Verified"       value={complete}   icon={<svg width="28" height="28" fill="none" stroke="#10b981" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>} color="#10b981" />
              <StatCard label="Currently Active"     value={inProgress} icon={<svg width="28" height="28" fill="none" stroke="#3b82f6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>} color="#3b82f6" />
              <StatCard label="Verification Pending" value={missing}    icon={<svg width="28" height="28" fill="none" stroke="#f59e0b" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>} color="#f59e0b" />
            </div>

            {/* ── FILTERS ── */}
            <div style={{
              background: "#fff", borderRadius: 16, padding: "24px",
              boxShadow: "0 4px 20px rgba(0,0,0,.04)", marginBottom: 24,
              display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end"
            }}>
              {/* Search */}
              <div style={{ flex: "1 1 220px" }}>
                <label style={labelStyle}>
                  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{marginRight: 6, verticalAlign: "text-top"}}><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                  Search Anything
                </label>
                <input
                  placeholder="Lot ID, Supervisor, Process…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={inputStyle}
                />
              </div>

              {/* Supervisor */}
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>Supervisor</label>
                <Select
                  value={fSupervisor}
                  onChange={setFSupervisor}
                  options={filters.supervisors.map(s => ({ value: s, label: s }))}
                  isClearable
                  placeholder="All Supervisors"
                  styles={selectStyles}
                />
              </div>

              {/* Process */}
              <div style={{ flex: "1 1 200px" }}>
                <label style={labelStyle}>Process</label>
                <Select
                  value={fProcess}
                  onChange={setFProcess}
                  options={filters.processes.map(p => ({ value: p, label: p }))}
                  isClearable
                  placeholder="All Processes"
                  styles={selectStyles}
                />
              </div>

              {/* Status */}
              <div style={{ flex: "1 1 150px" }}>
                <label style={labelStyle}>Status</label>
                <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={inputStyle}>
                  <option value="">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              {/* Date From */}
              <div style={{ flex: "1 1 160px" }}>
                <label style={labelStyle}>Date From</label>
                <input type="date" value={fDateFrom} onChange={e => setFDateFrom(e.target.value)} style={inputStyle} />
              </div>

              {/* Date To */}
              <div style={{ flex: "1 1 160px" }}>
                <label style={labelStyle}>Date To</label>
                <input type="date" value={fDateTo} onChange={e => setFDateTo(e.target.value)} style={inputStyle} />
              </div>

              {/* Clear */}
              <button
                onClick={() => { setFSupervisor(null); setFProcess(null); setFDateFrom(""); setFDateTo(""); setFStatus(""); setSearch(""); }}
                style={{
                  background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
                  borderRadius: 10, padding: "10px 20px",
                  fontSize: 14, fontWeight: 600, cursor: "pointer",
                  alignSelf: "flex-end", transition: "all 0.2s", height: 42
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#fee2e2"}
                onMouseLeave={e => e.currentTarget.style.background = "#fef2f2"}
              >✕ Clear</button>
            </div>

            {/* ── TABLE ── */}
            <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 4px 20px rgba(0,0,0,.04)", overflow: "hidden" }}>
              {/* Table header info */}
              <div style={{
                padding: "16px 24px", borderBottom: "1px solid #f3f4f6",
                display: "flex", alignItems: "center", justifyContent: "space-between",
                background: "#f8fafc"
              }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#1f2937", display: "flex", alignItems: "center", gap: 8 }}>
                  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path></svg>
                  Records ({loading ? "..." : visible.length})
                </span>
                
                {selectedIds.length > 0 && (
                  <button 
                    onClick={handleDeleteSelected}
                    style={{
                      background: "#ef4444", color: "white", padding: "6px 14px", 
                      borderRadius: 6, border: "none", fontSize: 13, fontWeight: 600,
                      cursor: "pointer", boxShadow: "0 2px 4px rgba(239,68,68,0.2)",
                      display: "flex", alignItems: "center", gap: 6, marginLeft: "auto", marginRight: 16
                    }}
                  >
                    <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                    Delete Selected ({selectedIds.length})
                  </button>
                )}
                
                <span style={{ fontSize: 13, color: "#64748b", fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path></svg>
                  Click any photo to enlarge
                </span>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: "#1f2937" }}>
                      <th style={{ ...thStyle, width: 40, textAlign: "center", padding: "0 8px" }}>
                        <input 
                          type="checkbox" 
                          style={{ cursor: "pointer", width: 16, height: 16, accentColor: "#3b82f6" }}
                          checked={paginatedRows.length > 0 && selectedIds.length === paginatedRows.length}
                          onChange={e => {
                            if (e.target.checked) setSelectedIds(paginatedRows.map(r => r.ID));
                            else setSelectedIds([]);
                          }}
                        />
                      </th>
                      {["#", "Lot ID", "Supervisor", "Process", "Qty", "New Process Time", "Confirm Time", "Completed Time", "Confirmed By", "Before Photo", "After Photo", "Status", "Action"].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={14} style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
                          <div style={{ display: "inline-block", width: 40, height: 40, border: "3px solid #e5e7eb", borderTopColor: "#3b82f6", borderRadius: "50%", animation: "spin 1s linear infinite" }}></div>
                          <div style={{ marginTop: 16, fontSize: 15, fontWeight: 500, color: "#6b7280" }}>Loading photo records...</div>
                          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </td>
                      </tr>
                    ) : visible.length === 0 ? (
                      <tr>
                        <td colSpan={14} style={{ textAlign: "center", padding: 80, color: "#9ca3af" }}>
                          <div style={{ marginBottom: 16 }}>
                            <svg width="48" height="48" fill="none" stroke="#9ca3af" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                            </svg>
                          </div>
                          <div style={{ fontSize: 18, fontWeight: 600, color: "#4b5563" }}>No records found</div>
                          <div style={{ fontSize: 14, marginTop: 4 }}>Try adjusting your filters or search query.</div>
                        </td>
                      </tr>
                    ) : paginatedRows.map((row, i) => (
                      <tr key={row.ID} style={{
                        borderBottom: "1px solid #f3f4f6",
                        background: i % 2 === 0 ? "#fff" : "#f8fafc",
                        transition: "all 0.15s ease"
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = "#eff6ff"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#f8fafc"; }}
                      >
                        <td style={{ ...tdStyle, textAlign: "center", padding: "0 8px" }}>
                          <input 
                            type="checkbox" 
                            style={{ cursor: "pointer", width: 16, height: 16, accentColor: "#3b82f6" }}
                            checked={selectedIds.includes(row.ID)}
                            onChange={e => {
                              const id = row.ID;
                              if (e.target.checked) setSelectedIds(prev => [...prev, id]);
                              else setSelectedIds(prev => prev.filter(x => x !== id));
                            }}
                          />
                        </td>
                        <td style={{ ...tdStyle, color: "#94a3b8", fontWeight: 500 }}>{row.ID}</td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: "#1d4ed8", fontFamily: "'Fira Code', monospace", fontSize: 13, letterSpacing: "-0.3px" }}>{row.LotID}</td>
                        <td style={{ ...tdStyle, fontWeight: 500 }}>{row.SupervisorName || "—"}</td>
                        <td style={tdStyle}>
                          <span style={{ background: "#f3e8ff", color: "#6b21a8", padding: "4px 10px", borderRadius: 16, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}>
                            {row.ProcessName}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600 }}>{row.Quantity?.toLocaleString() || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: "#64748b" }}>{row.NewProcessTime || "—"}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: "#64748b" }}>{row.ConfirmTime || <span style={{ color: "#d97706", fontWeight: 600 }}>Pending</span>}</td>
                        <td style={{ ...tdStyle, fontSize: 12, color: "#64748b" }}>{row.CompletedTime || <span style={{ color: "#d97706", fontWeight: 600 }}>Pending</span>}</td>
                        <td style={{ ...tdStyle, fontSize: 13, fontWeight: 500 }}>{row.ConfirmBy || "—"}</td>

                        {/* Before Photo */}
                        <td style={{ ...tdStyle, textAlign: "center", padding: "8px 16px" }}>
                          <PhotoCell src={row.BeforePhoto} label="Before" onView={setLightbox} />
                        </td>

                        {/* After Photo */}
                        <td style={{ ...tdStyle, textAlign: "center", padding: "8px 16px" }}>
                          <PhotoCell src={row.AfterPhoto} label="After" onView={setLightbox} />
                        </td>

                        <td style={tdStyle}>
                          <StatusBadge status={row.PhotoStatus} />
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <button
                            onClick={() => handleDeleteSingle(row.ID)}
                            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#ef4444", padding: 4 }}
                            title="Delete Record"
                          >
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {!loading && visible.length > 0 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20, padding: "0 8px", flexWrap: "wrap", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 13, color: "#64748b", fontWeight: 600 }}>Rows per page:</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(e.target.value === "All" ? "All" : Number(e.target.value))}
                    style={{ ...inputStyle, width: 80, padding: "6px 10px", fontSize: 13 }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={30}>30</option>
                    <option value={50}>50</option>
                    <option value="All">All</option>
                  </select>
                  <span style={{ fontSize: 13, color: "#64748b" }}>
                    Showing {itemsPerPage === "All" ? 1 : (currentPage - 1) * itemsPerPage + 1} to {itemsPerPage === "All" ? visible.length : Math.min(currentPage * itemsPerPage, visible.length)} of {visible.length} entries
                  </span>
                </div>
                
                {itemsPerPage !== "All" && totalPages > 1 && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      style={{
                        padding: "8px 16px", borderRadius: 8, border: "1px solid #cbd5e1",
                        background: currentPage === 1 ? "#f8fafc" : "#fff",
                        color: currentPage === 1 ? "#94a3b8" : "#334155",
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        fontWeight: 600, fontSize: 13, transition: "all 0.2s"
                      }}
                    >
                      Prev
                    </button>
                    <div style={{ display: "flex", alignItems: "center", padding: "0 12px", fontSize: 13, fontWeight: 700, color: "#475569" }}>
                      Page {currentPage} of {totalPages}
                    </div>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      style={{
                        padding: "8px 16px", borderRadius: 8, border: "1px solid #cbd5e1",
                        background: currentPage === totalPages ? "#f8fafc" : "#fff",
                        color: currentPage === totalPages ? "#94a3b8" : "#334155",
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                        fontWeight: 600, fontSize: 13, transition: "all 0.2s"
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 700,
  color: "#4b5563", marginBottom: 8, textTransform: "uppercase", letterSpacing: 0.5
};
const inputStyle = {
  width: "100%", padding: "10px 14px", borderRadius: 10,
  border: "1px solid #d1d5db", fontSize: 14,
  outline: "none", background: "#f9fafb", color: "#1f2937",
  boxSizing: "border-box", transition: "all 0.2s",
  fontFamily: "inherit"
};
const thStyle = {
  padding: "16px 16px", textAlign: "left",
  fontSize: 12, fontWeight: 700, color: "#ffffff",
  textTransform: "uppercase", letterSpacing: 0.5,
  whiteSpace: "nowrap", borderBottom: "none"
};
const tdStyle = {
  padding: "16px 16px", verticalAlign: "middle",
  color: "#334155", whiteSpace: "nowrap"
};

const selectStyles = {
  control: (base) => ({
    ...base,
    borderRadius: 10,
    border: "1px solid #d1d5db",
    boxShadow: "none",
    padding: "2px 0",
    fontSize: 14,
    background: "#f9fafb",
    '&:hover': {
      borderColor: "#9ca3af"
    }
  }),
  menu: (base) => ({
    ...base,
    fontSize: 14,
    zIndex: 9999
  })
};
